import { Box } from '@mui/material';
import { TournamentStatus, TournamentTeamResultEntity } from '@okletsplay/core';
import { TournamentEntity } from 'app/interfaces/entity';
import { TournamentSettingsTitle } from 'modules/tournaments/components/TournamentSettingsTitle';
import { Place } from 'modules/tournaments/components/TournamentModal/TournamentPrizes/Place';
import React, { FC, useCallback } from 'react';
import { UserAvatar } from 'shared/components/UserAvatar';
import { useStyles } from './MyCurrentRankings.styles';
import { useTournamentCurrentPrizes } from 'modules/tournaments/hooks';
import { GameCurrency } from 'modules/game-payments/components';

interface MyCurrentRankingsProps {
  tournament: TournamentEntity;
}

export const MyCurrentRankings: FC<MyCurrentRankingsProps> = ({ tournament }) => {
  const { classes } = useStyles();

  const getFirstUserFromTeam = useCallback(
    (teamId) => tournament.teams.find((team) => team.id === teamId)?.players[0].user,
    [tournament.teams]
  );

  const { currentDistributionBreakdown } = useTournamentCurrentPrizes(tournament);

  const getPrizeAmount = useCallback(
    (result: TournamentTeamResultEntity) => {
      if (tournament.status === TournamentStatus.Finished) {
        return result.playerPrize;
      } else {
        return currentDistributionBreakdown?.find((item) => item.places.includes(result.place))
          ?.amount;
      }
    },
    [currentDistributionBreakdown, tournament.status]
  );

  if (tournament?.results?.length === 0) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <TournamentSettingsTitle>
        {tournament.status === TournamentStatus.Finished ? 'Results' : 'My Current Rankings'}
      </TournamentSettingsTitle>
      <Box className={classes.list}>
        {tournament?.results?.map((result) => {
          const user = getFirstUserFromTeam(result.teamId);

          return (
            <Box
              key={`${result.createdAt}-${result?.teamId}-${result?.score}-${result?.place}`}
              className={classes.item}
            >
              <Box flex={2}>
                <Place places={[result?.place]} />
              </Box>
              {user && (
                <Box className={classes.usernameBlock} flex={3}>
                  <UserAvatar user={user} />
                  <Box className={classes.username}>{user.userName}</Box>
                </Box>
              )}
              <Box flex={2} className={classes.score}>
                Score: {result.score}
              </Box>
              <Box flex={2} className={classes.prize}>
                <GameCurrency amount={getPrizeAmount(result) ?? 0} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
